import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useNewsletterCampaign } from '../../hooks/useNewsletterCampaigns';

const PAGE_CONTENT_SX = {
  py: { xs: 2, md: 3 },
  px: { xs: 2, sm: 3 },
};

function MetricRow({ label, value }) {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell align="right">{value}</TableCell>
    </TableRow>
  );
}

function NewsletterCampaignDetailPage() {
  const { campaignId } = useParams();
  const { campaign, loading, error } = useNewsletterCampaign(campaignId);

  return (
    <DashboardLayout title="Newsletter Campaign">
      <Box sx={PAGE_CONTENT_SX}>
        <Typography
          component={RouterLink}
          to="/newsletter-campaigns"
          variant="body2"
          sx={{ color: 'primary.main', display: 'inline-block', mb: 2 }}
        >
          ← Newsletter Campaigns
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        ) : null}

        {campaign ? (
          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {campaign.issueName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip size="small" label={campaign.status} />
                <Chip
                  size="small"
                  color={
                    campaign.metricsStatus === 'final' ? 'success' : 'warning'
                  }
                  label={`metrics: ${campaign.metricsStatus}`}
                />
              </Stack>
              {campaign.metricsStatus !== 'final' ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Metrics are still updating from Mailgun events and should not
                  be treated as final.
                </Alert>
              ) : null}
              <Typography variant="body1">
                <strong>Subject:</strong> {campaign.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Preview:</strong> {campaign.previewText}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Sent:</strong> {campaign.sentAt || '—'}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Delivery &amp; engagement
              </Typography>
              <Table size="small">
                <TableBody>
                  <MetricRow label="Audience size" value={campaign.audienceSize} />
                  <MetricRow label="Sent" value={campaign.metrics.sent} />
                  <MetricRow
                    label="Delivered"
                    value={campaign.metrics.delivered}
                  />
                  <MetricRow label="Bounced" value={campaign.metrics.bounced} />
                  <MetricRow
                    label="Unique clickers"
                    value={campaign.metrics.uniqueClickers}
                  />
                  <MetricRow
                    label="Total clicks"
                    value={campaign.metrics.totalClicks}
                  />
                  <MetricRow
                    label="Unsubscribes"
                    value={campaign.metrics.unsubscribes}
                  />
                  <MetricRow
                    label="Spam complaints"
                    value={campaign.metrics.spamComplaints}
                  />
                  <MetricRow
                    label="Delivery failures"
                    value={campaign.metrics.deliveryFailures}
                  />
                  {campaign.metrics.opens != null ? (
                    <MetricRow
                      label="Opens (informational)"
                      value={campaign.metrics.opens}
                    />
                  ) : null}
                </TableBody>
              </Table>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Clicks by URL
              </Typography>
              {campaign.metrics.clickBreakdown.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tracked link clicks yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>URL</TableCell>
                      <TableCell align="right">Clicks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaign.metrics.clickBreakdown.map((entry) => (
                      <TableRow key={entry.url}>
                        <TableCell>{entry.url}</TableCell>
                        <TableCell align="right">{entry.clicks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Stack>
        ) : null}
      </Box>
    </DashboardLayout>
  );
}

export default NewsletterCampaignDetailPage;
